import { useRef, useState } from 'react'
import type { Kopecks } from '@/shared/lib'
import { cn } from '@/shared/lib'
import { MoneyAmount } from '@/shared/ui/money-amount'
import { MoneyInput } from '@/shared/ui/money-input'

interface InlineMoneyProps {
  value: Kopecks | null
  onSave: (value: Kopecks | null) => void
  ariaLabel: string
  saving?: boolean
  readClassName?: string
}

// «Тихий инлайн» суммы: значение — обычный текст, аффорданс на hover; клик →
// компактный MoneyInput. Enter/blur — сохранить, Esc — отмена.
export function InlineMoney({ value, onSave, ariaLabel, saving, readClassName }: InlineMoneyProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Kopecks | null>(value)
  const cancelled = useRef(false)

  function commit() {
    if (cancelled.current) {
      cancelled.current = false
      return
    }
    setEditing(false)
    if (draft !== value) onSave(draft)
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value)
          setEditing(true)
        }}
        title={`${ariaLabel}: изменить`}
        {...(value == null ? { 'aria-label': `${ariaLabel}: задать` } : {})}
        className={cn(
          '-mx-2 -my-0.5 inline-flex rounded-control px-2 py-0.5 text-left align-baseline underline-offset-4 transition-colors hover:bg-bg-elevated hover:underline hover:decoration-dotted hover:decoration-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          value == null && 'text-text-tertiary',
          readClassName,
        )}
      >
        {value == null ? '—' : <MoneyAmount kopecks={value} className="text-base" />}
      </button>
    )
  }

  return (
    // Инпут появляется по явному клику — фокусируем внутренний input по mount.
    <span ref={(el) => el?.querySelector('input')?.focus()}>
      <MoneyInput
        compact
        aria-label={ariaLabel}
        value={draft}
        disabled={saving}
        onChange={setDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') {
            cancelled.current = true
            setEditing(false)
          }
        }}
        onBlur={commit}
        placeholder="0"
      />
    </span>
  )
}
